import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Scale, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string { return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`; }
const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: any; label: string }> = {
  matched: { color: 'text-green-500', bg: 'bg-green-500/10', icon: CheckCircle2, label: 'Matched' },
  minor_variance: { color: 'text-yellow-500', bg: 'bg-yellow-500/10', icon: AlertTriangle, label: 'Minor Variance' },
  discrepancy: { color: 'text-red-500', bg: 'bg-red-500/10', icon: XCircle, label: 'Discrepancy' },
  missing_report: { color: 'text-blue-500', bg: 'bg-blue-500/10', icon: Info, label: 'Missing from Report' },
  missing_tb: { color: 'text-purple-500', bg: 'bg-purple-500/10', icon: Info, label: 'Missing from TB' },
};

export default function ReconciliationPage() {
  const [selectedClientId, setSelectedClientId] = useState('');

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-recon'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = (clientsData?.clients || clientsData || []).filter((c: any) => c.xeroOrgId || c.xero_org_id);

  const { data, isLoading } = useQuery({
    queryKey: ['reconciliation', selectedClientId],
    queryFn: async () => { const r = await apiClient.get(`/reconciliation/check/${selectedClientId}`); return r.data.data; },
    enabled: !!selectedClientId,
  });

  const recon = data?.reconciliation || [];
  const issues = data?.issues || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Scale}
        title="Auto-Reconciliation Checker"
        description="Compare Xero Trial Balance against P&L and Balance Sheet — flag discrepancies"
        iconColor="bg-purple-500/10 text-purple-500"
      />


      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium whitespace-nowrap">Select Client:</label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[350px]"><SelectValue placeholder="Choose a Xero-connected client..." /></SelectTrigger>
              <SelectContent className="max-h-[300px]">{clients.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading && <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /><span className="ml-3 text-sm text-muted-foreground">Running reconciliation...</span></div>}

      {data?.summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">Total Accounts</p>
              <p className="text-lg font-bold">{data.summary.totalAccounts}</p>
            </CardContent></Card>
            <Card className="border-green-500/30"><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-green-500 uppercase">Matched</p>
              <p className="text-lg font-bold text-green-500">{data.summary.matched} ({data.summary.matchRate}%)</p>
            </CardContent></Card>
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">Minor Variances</p>
              <p className="text-lg font-bold text-yellow-500">{data.summary.minorVariances}</p>
            </CardContent></Card>
            <Card className="border-red-500/30"><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-red-500 uppercase">Discrepancies</p>
              <p className="text-lg font-bold text-red-500">{data.summary.discrepancies}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">TB Balance</p>
              <p className={cn('text-lg font-bold', data.trialBalance?.isBalanced ? 'text-green-500' : 'text-red-500')}>
                {data.trialBalance?.isBalanced ? 'Balanced' : `Off by ${fmtCurrency(data.trialBalance?.imbalance || 0)}`}
              </p>
            </CardContent></Card>
          </div>

          {/* Issues */}
          {issues.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Reconciliation Findings</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.map((issue: any, i: number) => {
                  const Icon = issue.type === 'error' ? XCircle : issue.type === 'warning' ? AlertTriangle : Info;
                  return (
                    <div key={i} className={cn('rounded-lg p-3 border-l-4', issue.type === 'error' ? 'border-l-red-500 bg-red-500/5' : issue.type === 'warning' ? 'border-l-yellow-500 bg-yellow-500/5' : 'border-l-blue-500 bg-blue-500/5')}>
                      <p className="text-xs font-semibold flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{issue.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{issue.detail}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Reconciliation Table */}
          {recon.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Accounts Requiring Attention</CardTitle>
                <CardDescription className="text-xs">{data.matchedCount} accounts matched perfectly (not shown). Period: {data.period?.start} to {data.period?.end}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="px-3 py-2 text-left font-medium">Account</th>
                      <th className="px-3 py-2 text-right font-medium">Trial Balance</th>
                      <th className="px-3 py-2 text-right font-medium">Report</th>
                      <th className="px-3 py-2 text-right font-medium">Difference</th>
                      <th className="px-3 py-2 text-center font-medium">Status</th>
                      <th className="px-3 py-2 text-left font-medium">Suggestion</th>
                    </tr></thead>
                    <tbody>
                      {recon.map((item: any, idx: number) => {
                        const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.matched;
                        const Icon = cfg.icon;
                        return (
                          <tr key={idx} className="border-b hover:bg-muted/20">
                            <td className="px-3 py-1.5 font-medium">{item.account}</td>
                            <td className="px-3 py-1.5 text-right">{fmtCurrency(item.trialBalance)}</td>
                            <td className="px-3 py-1.5 text-right">{fmtCurrency(item.report)}</td>
                            <td className={cn('px-3 py-1.5 text-right font-medium', item.difference > 0 ? cfg.color : '')}>{item.difference > 0 ? fmtCurrency(item.difference) : '-'}</td>
                            <td className="px-3 py-1.5 text-center"><span className={cn('inline-flex items-center gap-1 text-[10px]', cfg.color)}><Icon className="h-3 w-3" />{cfg.label}</span></td>
                            <td className="px-3 py-1.5 text-muted-foreground text-[10px] max-w-[250px]">{item.suggestion || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
